import React, { useMemo, useState } from "react";
import { Collapse, CircularProgress } from "@mui/material";
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Copy,
  Info,
  RefreshCw,
  XCircle,
} from "lucide-react";
import styles from "./RunMetricsPanel.module.scss";

function tryParseJsonString(str) {
  if (typeof str !== "string") return null;
  const trimmed = str.trim();
  if (!trimmed) return null;
  if (!(trimmed.startsWith("{") || trimmed.startsWith("["))) return null;
  try {
    return JSON.parse(trimmed);
  } catch (e) {
    return null;
  }
}

function normalizeJson(value) {
  if (typeof value === "string") {
    const first = tryParseJsonString(value);
    if (first === null) return { raw: value };
    if (typeof first === "string") {
      const second = tryParseJsonString(first);
      return second ?? { raw: first };
    }
    return first;
  }
  if (value === undefined) return {};
  if (value === null) return {};
  return value;
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function syntaxHighlightJson(obj) {
  let jsonString = "";
  try {
    jsonString = JSON.stringify(obj ?? {}, null, 2);
  } catch (e) {
    jsonString = String(obj ?? "");
  }

  const escaped = escapeHtml(jsonString);
  return escaped.replace(
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\btrue\b|\bfalse\b|\bnull\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g,
    (match) => {
      let cls = "number";
      if (/^"/.test(match)) {
        cls = /:$/.test(match) ? "key" : "string";
      } else if (/true|false/.test(match)) {
        cls = "boolean";
      } else if (/null/.test(match)) {
        cls = "null";
      }
      return `<span class="json-${cls}">${match}</span>`;
    }
  );
}

function JsonBlock({ title, value, onCopy }) {
  const normalized = useMemo(() => normalizeJson(value), [value]);
  const raw = useMemo(() => {
    try {
      return JSON.stringify(normalized ?? {}, null, 2);
    } catch (e) {
      return String(normalized ?? "");
    }
  }, [normalized]);

  return (
    <div>
      <div className={styles.jsonBlockHeader}>
        <span className={styles.jsonBlockTitle}>{title}</span>
        <button type="button" className={styles.copyJsonBtn} onClick={() => onCopy(raw)}>
          <Copy size={12} strokeWidth={2} aria-hidden />
          COPY JSON
        </button>
      </div>
      <pre
        className={styles.jsonPre}
        dangerouslySetInnerHTML={{ __html: syntaxHighlightJson(normalized) }}
      />
    </div>
  );
}

export default function RunMetricsPanel({
  runId,
  loading,
  error,
  response,
  onRefresh,
  onToast,
  title = "Run details",
}) {
  const [detailsOpen, setDetailsOpen] = useState(true);
  const data = response?.data ?? null;
  const additionalMetrics = useMemo(() => normalizeJson(data?.additional_metrics), [data?.additional_metrics]);
  const reasoningTokens =
    data?.reasoning_tokens ??
    data?.reasoningTokens ??
    additionalMetrics?.reasoning_tokens ??
    additionalMetrics?.reasoningTokens ??
    null;

  const tokenAccountingNote = useMemo(() => {
    const input = Number(data?.input_tokens);
    const output = Number(data?.output_tokens);
    const total = Number(data?.total_tokens);
    const reasoning = Number(reasoningTokens);

    const hasIO = Number.isFinite(input) && Number.isFinite(output);
    const hasTotal = Number.isFinite(total);
    const hasReasoning = Number.isFinite(reasoning);

    if (!hasReasoning) return null;
    if (!hasIO || !hasTotal) {
      return "Note: token accounting varies by provider. Reasoning tokens may be billed and/or reported separately from total tokens.";
    }

    const sumIO = input + output;
    const sumIOR = input + output + reasoning;

    if (total === sumIOR) {
      return "Note: For this run, reasoning tokens are already included in total tokens (total = input + output + reasoning).";
    }
    if (total === sumIO && total !== sumIOR) {
      return "Note: For this run, reasoning tokens are reported separately and are NOT included in total tokens (total = input + output).";
    }

    return "Note: token accounting varies by provider. Reasoning tokens may be included in output/total, or reported separately depending on model/provider and backend implementation.";
  }, [data?.input_tokens, data?.output_tokens, data?.total_tokens, reasoningTokens]);

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      onToast?.("JSON copied", "success");
    } catch (e) {
      onToast?.("Failed to copy JSON", "error");
    }
  };

  const fmt = (v) => (v === null || v === undefined || v === "" ? "-" : v);

  return (
    <div className={styles.root}>
      <div className={styles.card}>
        <div className={styles.headerRow}>
          <div className={styles.headerText}>
            <h3 className={styles.title}>{title}</h3>
            {runId ? (
              <p className={styles.runId}>run_id: {runId}</p>
            ) : (
              <p className={styles.runId}>run_id: —</p>
            )}
          </div>
          <button
            type="button"
            className={styles.refreshBtn}
            disabled={!runId || loading}
            onClick={() => runId && onRefresh?.()}
          >
            <RefreshCw size={12} strokeWidth={2} aria-hidden />
            REFRESH
          </button>
        </div>

        {!runId ? (
          <p className={`${styles.stateMessage} ${styles.alertInfo}`}>Run the agent once to see details for that run.</p>
        ) : (
          <>
            {error ? <p className={styles.alertError}>{error}</p> : null}

            {loading ? (
              <div className={styles.loadingRow}>
                <CircularProgress size={18} sx={{ color: "#e36250" }} />
                <span>Loading run data...</span>
              </div>
            ) : data ? (
              <>
                <div className={styles.badges}>
                  <span className={styles.badgeTotal}>Total tokens: {fmt(data.total_tokens)}</span>
                  <span className={styles.badgeOutline}>Input: {fmt(data.input_tokens)}</span>
                  <span className={styles.badgeOutline}>Output: {fmt(data.output_tokens)}</span>
                  <span className={styles.badgeOutline}>Reasoning: {fmt(reasoningTokens)}</span>
                  <span className={styles.badgeOutline}>
                    <span className={styles.modelLabel}>Model:</span> {fmt(data.model_id)}
                  </span>
                  <span className={styles.badgeOutline}>Duration: {data.duration_ms != null ? `${data.duration_ms}ms` : "-"}</span>
                  <span className={styles.badgeOutline}>Tool calls: {fmt(data.tool_calls)}</span>
                </div>

                <div className={styles.statusRow}>
                  {data.success === true ? (
                    <span className={styles.successBadge}>
                      <CheckCircle2 size={12} strokeWidth={2} aria-hidden />
                      Success: true
                    </span>
                  ) : data.success === false ? (
                    <span className={`${styles.successBadge} ${styles.errorBadge}`}>
                      <XCircle size={12} strokeWidth={2} aria-hidden />
                      Success: false
                    </span>
                  ) : (
                    <span className={styles.badgeOutline}>Success: —</span>
                  )}
                </div>

                {tokenAccountingNote ? (
                  <div className={styles.infoBox}>
                    <Info className={styles.infoBoxIcon} size={16} strokeWidth={2} aria-hidden />
                    <p className={styles.infoBoxText}>{tokenAccountingNote}</p>
                  </div>
                ) : null}

                <div className={styles.summaryGrid}>
                  <div className={styles.summaryCard}>
                    <span className={styles.summaryLabel}>Usage</span>
                    <h4 className={styles.summaryTitle}>This run used {fmt(data.total_tokens)} tokens</h4>
                    <p className={styles.summaryMeta}>
                      input {fmt(data.input_tokens)} • output {fmt(data.output_tokens)}
                      {reasoningTokens !== null && reasoningTokens !== undefined
                        ? ` • reasoning ${reasoningTokens}`
                        : ""}
                    </p>
                  </div>
                  <div className={styles.summaryCard}>
                    <span className={styles.summaryLabel}>Runtime</span>
                    <h4 className={styles.summaryTitle}>
                      {data.duration_ms != null ? `${data.duration_ms} ms` : "—"}
                    </h4>
                    <p className={styles.summaryMeta}>
                      model {fmt(data.model_id)} • tool calls {fmt(data.tool_calls)}
                    </p>
                  </div>
                </div>

                <div className={styles.detailsSection}>
                  <button
                    type="button"
                    className={styles.detailsToggle}
                    onClick={() => setDetailsOpen((o) => !o)}
                    aria-expanded={detailsOpen}
                  >
                    Additional details
                    {detailsOpen ? (
                      <ChevronUp size={16} strokeWidth={2} aria-hidden />
                    ) : (
                      <ChevronDown size={16} strokeWidth={2} aria-hidden />
                    )}
                  </button>
                  <Collapse in={detailsOpen} timeout="auto" unmountOnExit={false}>
                    <div className={`${styles.stackGap} ${styles.detailsInner}`}>
                      <JsonBlock title="additional_metrics" value={data.additional_metrics ?? {}} onCopy={handleCopy} />
                      <div className={styles.jsonBlockDivider}>
                        <JsonBlock title="Full metrics JSON" value={data ?? {}} onCopy={handleCopy} />
                      </div>
                    </div>
                  </Collapse>
                </div>
              </>
            ) : !error ? (
              <p className={`${styles.stateMessage} ${styles.alertWarn}`}>No data available for this run yet.</p>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
